import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import heroBg from '../../assets/sas_hero_new.png';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';
import { motion } from 'framer-motion';
import HeroBackground from '../../components/ui/HeroBackground';

export default function ContactPage() {
  const t = useT();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = e => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      toast.success(t('messageSent'));
      setForm({ name: '', email: '', subject: '', message: '' });
      setLoading(false);
    }, 1000);
  };

  return (
    <>
      <div className="hero-premium" style={{ minHeight: '400px', padding: '120px 2rem 100px' }}>
        <HeroBackground />

          <div style={{ position: 'relative', zIndex: 10 }}>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-gradient-gold"
              style={{ fontSize: 56, fontWeight: 800, marginBottom: 16, letterSpacing: -1 }}
            >
              {t('contactUs')}
            </motion.h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 18, maxWidth: 600, margin: '0 auto', fontWeight: 500, lineHeight: 1.6 }}>
              Have a question or want to get involved? Reach out to our central office or find a local branch near you.
            </p>
          </div>
        </div>

      <div style={{
        background: 'var(--sindhi-ivory)',
        backgroundImage: `url(${ajrakTexture})`,
        backgroundSize: '600px 600px',
        backgroundRepeat: 'repeat',
        position: 'relative',
        zIndex: 10,
        marginTop: '-40px'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          background: 'rgba(253,248,235,0.7)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '64px 24px 100px',
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: 56
        }}>
          {/* Info Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {[
              { icon: '📍', title: t('headOffice'), info: 'Karachi, Sindh, Pakistan' },
              { icon: '📞', title: t('phone_label'), info: '+92-21-XXXXXXXX' },
              { icon: '✉️', title: t('email_label'), info: 'info@sindhiadabisangat.com' },
              { icon: '🕐', title: t('officeHours'), info: t('officeHoursDesc') },
            ].map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-royal"
                style={{
                  padding: '32px',
                  display: 'flex',
                  gap: 24,
                  background: 'white',
                  border: '1px solid var(--sindhi-gold-muted)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  fontSize: 28,
                  width: 68, height: 68, borderRadius: 20,
                  background: 'linear-gradient(135deg, var(--sindhi-ivory), #fff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid var(--sindhi-gold-muted)',
                  boxShadow: '0 10px 20px -5px rgba(0,0,0,0.05)'
                }}>{c.icon}</div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div className="text-gradient-gold" style={{ fontSize: 13, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>{c.title}</div>
                  <div style={{ fontSize: 17, color: 'var(--sindhi-navy)', fontWeight: 700, whiteSpace: 'pre-line', lineHeight: 1.6 }}>{c.info}</div>
                </div>
              </motion.div>
            ))}

            {/* Sindhi Decorative Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              style={{
                padding: 40, borderRadius: 32, overflow: 'hidden', position: 'relative',
                background: 'linear-gradient(135deg, var(--sindhi-maroon), var(--sindhi-navy))', color: 'white',
                boxShadow: '0 30px 60px -15px var(--sindhi-maroon-glow)'
              }}
            >
              <div style={{ position: 'absolute', bottom: -20, right: -20, opacity: 0.1, fontSize: 180, transform: 'rotate(-10deg)' }}>س</div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ width: 50, height: 4, background: 'var(--sindhi-gold)', marginBottom: 24, borderRadius: 2 }} />
                <div style={{ fontFamily: 'var(--font-sindhi)', fontSize: 28, textAlign: 'center', lineHeight: 1.8, fontWeight: 800 }}>
                  سنڌي ادبي سنگت سنڌ<br />
                  <span style={{ fontSize: 16, opacity: 0.8, fontWeight: 400, fontFamily: 'var(--font-display)' }}>Dedicated to the Soul of Sindh</span>
                </div>
                <div style={{ width: 50, height: 4, background: 'var(--sindhi-gold)', marginTop: 24, borderRadius: 2, marginLeft: 'auto' }} />
              </div>
            </motion.div>
          </div>

          {/* Form Column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-royal"
            style={{
              padding: 56,
              background: 'white',
              border: '1px solid var(--sindhi-gold-muted)',
              boxShadow: '0 40px 100px -20px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ marginBottom: 40 }}>
              <h2 className="text-gradient-gold" style={{ fontSize: 32, fontWeight: 800, marginBottom: 12 }}>{t('sendAMessage')}</h2>
              <div style={{ width: 60, height: 4, background: 'var(--sindhi-maroon)', borderRadius: 2 }}></div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 12, fontSize: 14 }}>{t('yourName')}</label>
                  <input className="form-input" style={{ height: 56, borderRadius: 16, border: '1px solid var(--sindhi-ivory)', background: 'var(--sindhi-ivory)', padding: '0 20px', fontWeight: 600 }} value={form.name} onChange={e => set('name', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 12, fontSize: 14 }}>{t('email_label')}</label>
                  <input className="form-input" type="email" style={{ height: 56, borderRadius: 16, border: '1px solid var(--sindhi-ivory)', background: 'var(--sindhi-ivory)', padding: '0 20px', fontWeight: 600 }} value={form.email} onChange={e => set('email', e.target.value)} required />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label" style={{ fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 12, fontSize: 14 }}>{t('subject')}</label>
                  <input className="form-input" style={{ height: 56, borderRadius: 16, border: '1px solid var(--sindhi-ivory)', background: 'var(--sindhi-ivory)', padding: '0 20px', fontWeight: 600 }} value={form.subject} onChange={e => set('subject', e.target.value)} required />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label" style={{ fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 12, fontSize: 14 }}>{t('message')}</label>
                  <textarea className="form-input" rows={6} value={form.message}
                    onChange={e => set('message', e.target.value)} required
                    style={{ resize: 'none', borderRadius: 20, border: '1px solid var(--sindhi-ivory)', background: 'var(--sindhi-ivory)', padding: 24, fontWeight: 600, lineHeight: 1.6 }} />
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="btn-premium"
                style={{ width: '100%', height: 64, marginTop: 40, fontSize: 16, fontWeight: 800 }}
                disabled={loading}
              >
                {loading ? t('loading') : `${t('sendMessage')}`}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
}
